import { contrastRatio, hexToHslChannels, mixHex, withContrast } from "./color";
import type { GoogleFont } from "./googleFonts";

// A page's theme: the values of the design tokens its catalog components use.
// It is saved with every revision, next to the kyte definition.

export const THEME_COLORS = [
  "background",
  "foreground",
  "card",
  "muted",
  "muted-foreground",
  "primary",
  "primary-foreground",
  "secondary",
  "secondary-foreground",
  "accent",
  "accent-foreground",
  "border",
] as const;

export type ThemeColor = (typeof THEME_COLORS)[number];

// Colours derived from the theme's, for pale panels and highlights in the
// brand's colours: each is its base tinted towards the page's background.
const SUBTLE_COLORS = { "primary-subtle": "primary", "accent-subtle": "accent" } as const;

const SUBTLE_WEIGHT = 0.12;

// Every colour a catalog colour prop can name.
export const CATALOG_COLORS = [...THEME_COLORS, ...Object.keys(SUBTLE_COLORS)] as const;

// The hex a catalog colour name stands for in a theme, as the page renders
// it; undefined for a name that isn't one.
export function catalogColorHex(name: string, theme: PageTheme): string | undefined {
  if ((THEME_COLORS as readonly string[]).includes(name)) return theme.colors[name as ThemeColor];
  if (name in SUBTLE_COLORS) {
    const base = SUBTLE_COLORS[name as keyof typeof SUBTLE_COLORS];
    return mixHex(theme.colors.background, theme.colors[base], SUBTLE_WEIGHT);
  }
  return undefined;
}

// Font stacks of the visitor's own fonts, which load nothing.
export const SYSTEM_FONTS = {
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", serif',
  mono: '"geist-mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  rounded: 'ui-rounded, "SF Pro Rounded", "Nunito", system-ui, sans-serif',
} as const;

type SystemFont = keyof typeof SYSTEM_FONTS;

// A system font, or the family name of a Google font, e.g. "Playfair Display".
export type ThemeFont = string;

export function isSystemFont(font: string): font is SystemFont {
  return font in SYSTEM_FONTS;
}

// What a Google font's text shows until the font loads.
const FALLBACK_STACKS: Record<GoogleFont["category"], string> = {
  "sans-serif": SYSTEM_FONTS.sans,
  serif: SYSTEM_FONTS.serif,
  display: SYSTEM_FONTS.sans,
  handwriting: "cursive",
  monospace: SYSTEM_FONTS.mono,
};

// Looks a family up in the Google Fonts catalog. The browser loads the
// catalog lazily (`useFindGoogleFont`), so what renders a theme takes it as
// an argument.
export type FindGoogleFont = (family: string) => GoogleFont | undefined;

export function fontStack(font: ThemeFont, findFont: FindGoogleFont): string {
  if (isSystemFont(font)) return SYSTEM_FONTS[font];
  const google = findFont(font);
  return google ? `"${google.family}", ${FALLBACK_STACKS[google.category]}` : SYSTEM_FONTS.sans;
}

// The corners of surfaces (cards, pictures, boxes). "full" is the pill
// theme: its surfaces get generous corners and its controls are pills.
export const THEME_RADII = {
  none: "0rem",
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "1rem",
  full: "1.5rem",
} as const;

export type ThemeRadius = keyof typeof THEME_RADII;

// How heavy and how tightly set the page's headings are: a light, tightly
// tracked display face and a bold, airy one read as different brands.
export const THEME_HEADING_WEIGHTS = {
  light: "300",
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
} as const;

export type ThemeHeadingWeight = keyof typeof THEME_HEADING_WEIGHTS;

export const THEME_HEADING_TRACKINGS = {
  tighter: "-0.04em",
  tight: "-0.025em",
  normal: "0em",
  wide: "0.025em",
} as const;

export type ThemeHeadingTracking = keyof typeof THEME_HEADING_TRACKINGS;

// How large every heading is, relative to the catalog's sizes: an editorial
// page sets its titles larger than a dense product page. Past "large", only
// the large headings grow, on wider screens (typography.tsx).
export const THEME_HEADING_SCALES = {
  compact: "0.875",
  default: "1",
  large: "1.25",
  xlarge: "1.5",
  display: "1.875",
} as const;

export type ThemeHeadingScale = keyof typeof THEME_HEADING_SCALES;

// How much the page moves (the catalog's motion props: entrances, scroll
// effects, counters, marquees, hovers), and in what character. Neither is set
// on a theme made before pages moved: a page without motion props doesn't
// move whatever they say, and one with them moves "subtle" and "calm".
export const THEME_MOTIONS = ["none", "subtle", "expressive"] as const;

export type ThemeMotion = (typeof THEME_MOTIONS)[number];

export const THEME_MOTION_FEELS = ["calm", "snappy", "playful"] as const;

export type ThemeMotionFeel = (typeof THEME_MOTION_FEELS)[number];

// A Google family beyond the heading and body fonts, with the weights the
// page sets it in, e.g. a design's third family. Stylesheets set it as
// `var(--font-<slug>)` (`themeFontVariable`).
type ThemeExtraFont = { family: string; weights: number[] };

export const PALETTE_NAME = /^[a-z][a-z0-9-]{0,31}$/;

export const MAX_PALETTE_COLORS = 48;

export const MAX_EXTRA_FONTS = 6;

export type PageTheme = {
  // Hex colours, "#rrggbb".
  colors: Record<ThemeColor, string>;
  // Colours beyond the roles above, by name ("brand-500"): a design's own
  // palette, which stylesheets read as `var(--c-<name>)`.
  palette?: Record<string, string>;
  headingFont: ThemeFont;
  bodyFont: ThemeFont;
  // Code and technical labels; the system mono when unset or null.
  monoFont?: ThemeFont | null;
  fonts?: ThemeExtraFont[];
  radius: ThemeRadius;
  headingWeight: ThemeHeadingWeight;
  headingTracking: ThemeHeadingTracking;
  headingScale: ThemeHeadingScale;
  motion?: ThemeMotion;
  motionFeel?: ThemeMotionFeel;
};

export const defaultTheme: PageTheme = {
  colors: {
    background: "#ffffff",
    foreground: "#0a0a0a",
    card: "#ffffff",
    muted: "#f5f5f5",
    "muted-foreground": "#737373",
    primary: "#171717",
    "primary-foreground": "#fafafa",
    secondary: "#f5f5f5",
    "secondary-foreground": "#171717",
    accent: "#f5f5f5",
    "accent-foreground": "#171717",
    border: "#e5e5e5",
  },
  headingFont: "sans",
  bodyFont: "sans",
  radius: "md",
  headingWeight: "semibold",
  headingTracking: "tight",
  headingScale: "default",
};

// A blank page's theme (`Website.startBlank`): a calm serif for headings over
// a plain sans, ink on a warm off-white, and one quiet grey for what is
// muted. Near-neutral, so what the user or the agent makes of it reads as
// theirs. A curated pairing (`FONT_PAIRINGS`); every text colour reads 4.5:1
// on its ground.
export const blankTheme: PageTheme = {
  colors: {
    background: "#faf9f7",
    foreground: "#1c1b19",
    card: "#ffffff",
    muted: "#f1efec",
    "muted-foreground": "#5e5a55",
    primary: "#1c1b19",
    "primary-foreground": "#faf9f7",
    secondary: "#f1efec",
    "secondary-foreground": "#1c1b19",
    accent: "#e7e4df",
    "accent-foreground": "#1c1b19",
    border: "#e3e0db",
  },
  headingFont: "Newsreader",
  bodyFont: "Public Sans",
  radius: "md",
  headingWeight: "normal",
  headingTracking: "tight",
  headingScale: "large",
  motion: "subtle",
  motionFeel: "calm",
};

// The CSS variable of an extra font: its family in lowercase, words joined
// by hyphens ("DM Serif Display" is --font-dm-serif-display).
export function themeFontVariable(family: string) {
  return `--font-${family
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;
}

// The CSS variables that apply a theme to everything below the element that
// sets them: the catalog's tokens and the Tailwind colours its UI kit uses.
export function themeVariables(theme: PageTheme, findFont: FindGoogleFont): Record<string, string> {
  const channels = Object.fromEntries(
    THEME_COLORS.map((name) => [`--${name}`, hexToHslChannels(theme.colors[name])]),
  );
  const subtle = Object.fromEntries(
    Object.entries(SUBTLE_COLORS).map(([name, base]) => [
      `--${name}`,
      hexToHslChannels(mixHex(theme.colors.background, theme.colors[base], SUBTLE_WEIGHT)),
    ]),
  );
  // The focus ring (ui/button.tsx), drawn past a 2px gap in the page's
  // background: the first of the page's text colour, the primary's text
  // colour and the greys that stands 3:1 (WCAG 1.4.11) from the page and
  // from a primary button, or from the page where the gap already stands
  // 3:1 from the button. No colour stands 3:1 from both a white page and a
  // pure blue button, so the gap does there. Failing both, one that stands
  // from the page and most from the button.
  const { background, primary } = theme.colors;
  const stands = (a: string, b: string) => contrastRatio(a, b) >= 3;
  const greys = Array.from({ length: 21 }, (_, step) => mixHex("#000000", "#ffffff", step / 20));
  const candidates = [theme.colors.foreground, theme.colors["primary-foreground"], ...greys];
  const ring =
    candidates.find(
      (hex) => stands(hex, background) && (stands(hex, primary) || stands(background, primary)),
    ) ??
    candidates
      .filter((hex) => stands(hex, background))
      .reduce((best, hex) =>
        contrastRatio(hex, primary) > contrastRatio(best, primary) ? hex : best,
      );
  // Errors (a form's problem, a field's border) in red-600, darkened or
  // lightened until it reads as small text (4.5:1) on the page and on its
  // cards: a fixed red is too faint on a pale or a dark ground.
  const destructive = withContrast(
    withContrast("#dc2626", theme.colors.background),
    theme.colors.card,
  );
  return {
    ...channels,
    ...subtle,
    "--destructive": hexToHslChannels(destructive),
    "--destructive-foreground": hexToHslChannels(
      contrastRatio("#ffffff", destructive) >= contrastRatio("#0a0a0a", destructive)
        ? "#ffffff"
        : "#0a0a0a",
    ),
    "--card-foreground": channels["--foreground"]!,
    "--popover": channels["--card"]!,
    "--popover-foreground": channels["--foreground"]!,
    "--input": channels["--border"]!,
    "--ring": hexToHslChannels(ring),
    "--radius": THEME_RADII[theme.radius],
    "--radius-control": theme.radius === "full" ? "9999px" : "calc(var(--radius) - 2px)",
    "--font-heading": fontStack(theme.headingFont, findFont),
    "--font-body": fontStack(theme.bodyFont, findFont),
    // Code and technical labels: the theme's mono font, the same in the
    // builder and on the site (the builder's own mono is IBM Plex Mono).
    "--font-mono": fontStack(theme.monoFont ?? "mono", findFont),
    "--heading-weight": THEME_HEADING_WEIGHTS[theme.headingWeight],
    "--heading-tracking": THEME_HEADING_TRACKINGS[theme.headingTracking],
    "--heading-scale": THEME_HEADING_SCALES[theme.headingScale],
    ...Object.fromEntries(
      Object.entries(theme.palette ?? {}).map(([name, hex]) => [`--c-${name}`, hex]),
    ),
    ...Object.fromEntries(
      (theme.fonts ?? []).map(({ family }) => [
        themeFontVariable(family),
        fontStack(family, findFont),
      ]),
    ),
  };
}

// The stylesheet that loads the theme's Google fonts; null when it uses
// system fonts only. It is on the page's own host (`GoogleFonts`, on the
// app's host and every site's), so a visitor's browser never asks Google.
// The extra fonts are loaded in the weights the page sets them in only.
export function themeFontsUrl(theme: PageTheme, findFont: FindGoogleFont): string | null {
  const fonts = new Map<string, GoogleFont>();
  for (const family of [theme.headingFont, theme.bodyFont, theme.monoFont ?? "mono"]) {
    const font = findFont(family);
    if (font) fonts.set(font.family, font);
  }
  for (const { family, weights } of theme.fonts ?? []) {
    const font = findFont(family);
    if (!font || fonts.has(font.family)) continue;
    const loaded = font.weights.filter((weight) => weights.includes(weight));
    fonts.set(font.family, { ...font, weights: loaded.length > 0 ? loaded : font.weights });
  }
  if (fonts.size === 0) return null;
  const query = new URLSearchParams(
    [...fonts.values()].map(({ family, weights }) => [
      "family",
      `${family}:wght@${weights.join(";")}`,
    ]),
  );
  query.set("display", "swap");
  return `/api/fonts/css2?${query}`;
}
